// The ClearMakes product catalog.
//
// This file is the single source of truth for every place products appear:
// the homepage catalog, /products, the header menu, the footer and the
// "Get started" product picker. To launch a new product, add one entry here
// (and a page for it under src/pages) — nothing else needs to change.

export type ProductStatus = "live" | "beta" | "coming-soon";

export interface Product {
  /** URL segment: /products/<slug> */
  slug: string;
  name: string;
  /**
   * The product's own mark (square, transparent PNG under public/brand/).
   * Product lists show it; the ClearMakes "C" stays for the company itself.
   */
  logo: string;
  /** One line shown on cards and in menus. */
  tagline: string;
  /** A few words, for tight places like the app menu. */
  shortLine: string;
  /** Two or three sentences for the catalog card and meta description. */
  description: string;
  category: string;
  status: ProductStatus;
  /** Where "Get started" sends someone who picks this product. */
  startUrl: string;
  /** Where signed-in users open the product. */
  appUrl: string;
  pricingUrl?: string;
  /** Tools this product actually connects to (keys of `integrations`). */
  integrations: string[];
  /** Short capability list for cards. */
  highlights: string[];
  /**
   * A product that lives on its own site (agents.clearmakes.com). Menus and
   * cards link there instead of to /products/<slug>.
   */
  url?: string;
  /** What the drawn product preview on cards shows; Inbox's is the default. */
  preview?: ProductPreviewData;
  /**
   * A desktop app's download page. The homepage card shows a Download button
   * beside Learn more, and "Get started" offers the download instead of a workspace.
   */
  download?: { label: string; href: string };
}

export interface ProductPreviewData {
  nav: string[];
  /** `app` is an integration key for the row's logo; null shows none. */
  rows: { from: string; line: string; tag: string; app?: string | null }[];
  panel: { heading: string; body: string; subheading: string; box: string; primary: string; secondary: string };
}

export const products: Product[] = [
  {
    slug: "inbox",
    name: "ClearMakes Inbox",
    logo: "/brand/inbox-mark-256.png",
    shortLine: "Workflows and answers for your apps",
    tagline: "Workflows that act on new messages, and answers to everyday questions.",
    description:
      "Connect Gmail, Outlook, Slack, Teams, Notion, HubSpot and more. Build workflows that act on new messages, and let Smart FAQ answer the questions you get every day.",
    category: "Communication",
    status: "live",
    startUrl: "/login?view=signup",
    appUrl: "/platform",
    pricingUrl: "/pricing",
    integrations: ["gmail", "outlook", "slack", "teams", "google-calendar", "notion", "hubspot"],
    highlights: ["Workflows", "Apps", "Smart FAQ"],
    preview: {
      nav: ["Home", "Workflows", "Apps", "Smart FAQ", "Settings"],
      rows: [
        { from: "New email from a customer", line: "Summarised and posted to #support", tag: "Workflow", app: "gmail" },
        { from: "#sales", line: "New lead added to HubSpot", tag: "Workflow", app: "slack" },
        { from: "Smart FAQ", line: "What are your opening hours?", tag: "Answered", app: null },
        { from: "New Outlook email", line: "Reply drafted for you to check", tag: "Draft", app: "outlook" },
        { from: "Notion", line: "Meeting notes page created", tag: "Workflow", app: "notion" },
      ],
      panel: {
        heading: "Workflow",
        body: "When a new email arrives in Gmail, summarise it and post it to #support in Slack.",
        subheading: "Steps",
        box: "New email  ->  Summarise  ->  Post to Slack",
        primary: "Turn on",
        secondary: "Edit",
      },
    },
  },
  {
    slug: "agents",
    name: "ClearMakes Agents",
    logo: "/brand/agents-mark-256.png",
    shortLine: "AI agents for team and website",
    tagline: "AI agents for your team and your website.",
    description:
      "Build AI agents from your documents, website and Google Drive. Connect Gmail, Slack, HubSpot and hundreds of apps or any MCP server, let them answer your team and do the busywork, and put the same agent on your website in one line.",
    category: "AI agents",
    status: "live",
    url: "https://agents.clearmakes.com",
    startUrl: "https://agents.clearmakes.com/signup",
    appUrl: "https://agents.clearmakes.com/admin",
    pricingUrl: "https://agents.clearmakes.com/pricing",
    integrations: ["gmail", "slack", "outlook", "teams", "google-calendar"],
    highlights: ["Team agents", "Website chatbot", "MCP servers", "Hundreds of apps", "Google Drive sync", "Works in Claude and ChatGPT"],
    preview: {
      nav: ["Home", "Agents", "Conversations", "Knowledge", "Usage"],
      rows: [
        { from: "Support assistant", line: "Drafted 6 replies in Gmail", tag: "Done", app: "gmail" },
        { from: "Ops assistant", line: "Posted a summary to #support", tag: "Action", app: "slack" },
        { from: "Website chat", line: "Do you ship to Canada?", tag: "Answered", app: null },
        { from: "Sales copilot", line: "Booked a call for Thursday", tag: "Calendar", app: "google-calendar" },
        { from: "HR helpdesk", line: "How much parental leave do we get?", tag: "Answered", app: null },
      ],
      panel: {
        heading: "Knowledge",
        body: "12 documents, acme.com (48 pages) and the Operations folder in Google Drive.",
        subheading: "Put it on your website",
        box: '<script src="agents.clearmakes.com/agents.js">',
        primary: "Copy",
        secondary: "Share link",
      },
    },
  },
  {
    slug: "meet",
    name: "ClearMeet",
    logo: "/brand/meet-mark-256.png",
    shortLine: "Live answers in every meeting",
    tagline: "Answers and notes while the meeting is still happening.",
    description:
      "A quiet helper for Zoom, Teams, Google Meet and any call on Windows. ClearMeet hears the question, gives you a short answer in seconds, and writes the notes and summary for you. It stays hidden when you share your screen, and no bot joins the call.",
    category: "Meetings",
    status: "live",
    url: "https://meet.clearmakes.com",
    startUrl: "https://meet.clearmakes.com/download",
    appUrl: "https://meet.clearmakes.com/account",
    pricingUrl: "https://meet.clearmakes.com/pricing",
    download: { label: "Download", href: "https://meet.clearmakes.com/download" },
    integrations: [],
    highlights: ["Live answers", "Meeting notes", "Hidden in screen share", "Summaries", "No bot in the call"],
    preview: {
      nav: ["Home", "Answers", "Notes", "Files", "Settings"],
      rows: [
        { from: "Sam · Weekly pilot review", line: "What would a pilot cost for forty people?", tag: "Answered", app: "teams" },
        { from: "Summary", line: "30-day pilot for 40 people at $18 a seat", tag: "Notes", app: null },
        { from: "Action item", line: "Send the price list after the call", tag: "You", app: null },
        { from: "Price list.pdf", line: "Seat prices, page 2", tag: "Source", app: null },
        { from: "Screen share", line: "ClearMeet stays hidden", tag: "Private", app: null },
      ],
      panel: {
        heading: "Answer",
        body: "At $18 a seat, forty people come to $720 a month. The first 30 days are free.",
        subheading: "Summary",
        box: "Northwind wants a 30-day pilot for 40 support staff. Next: send the price list.",
        primary: "Copy",
        secondary: "Share",
      },
    },
  },
];

export const liveProducts = products.filter((p) => p.status !== "coming-soon");

export const getProduct = (slug: string) => products.find((p) => p.slug === slug);

export const productPath = (p: Product) => p.url ?? `/products/${p.slug}`;
